import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Center, HStack, Spinner, Text } from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import { SearchBox } from 'src/components/atoms/SearchBox';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { useAppContext } from 'src/contexts/AppContext';
import {
  BulkAction,
  ManagedUser,
  useUsersQuery,
  useInviteUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useResendInviteMutation,
  useBulkUsersMutation,
} from 'src/api/users';
import { CustomerRole } from 'src/contexts/AuthContext';
import { UsersTable } from '../molecules/UsersTable';
import { InviteUserModal } from '../molecules/InviteUserModal';
import { EditUserDrawer } from '../molecules/EditUserDrawer';
import { DeleteUserDialog } from '../molecules/DeleteUserDialog';
import { BulkActionsBar } from '../molecules/BulkActionsBar';

export const UserManagementTab: React.FC = () => {
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName ?? '';

  const { toastState, showSuccess, showError, hideToast } = useCustomToast();

  // Query hooks
  const { data: users = [], isLoading, isError } = useUsersQuery(customerId);

  // Mutation hooks
  const inviteUserMutation = useInviteUserMutation(customerId);
  const updateUserMutation = useUpdateUserMutation(customerId);
  const deleteUserMutation = useDeleteUserMutation(customerId);
  const resendInviteMutation = useResendInviteMutation(customerId);
  const bulkUsersMutation = useBulkUsersMutation(customerId);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedUser, setSelectedUser] = useState<ManagedUser | null>(null);

  // Pagination state
  const [pageSize, setPageSize] = useState(10);
  const [displayCount, setDisplayCount] = useState(10);

  // Modal/Dialog state
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Filtered users
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;

    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.email.toLowerCase().includes(query) ||
        user.displayName?.toLowerCase().includes(query),
    );
  }, [users, searchQuery]);

  // Paginated users (slice of filtered)
  const paginatedUsers = useMemo(() => {
    return filteredUsers.slice(0, displayCount);
  }, [filteredUsers, displayCount]);

  const hasMoreUsers = displayCount < filteredUsers.length;

  // Reset pagination when search changes
  useEffect(() => {
    setDisplayCount(pageSize);
  }, [searchQuery, pageSize]);

  // Check if any selected users are pending (for bulk resend)
  const hasPendingInSelection = useMemo(() => {
    return Array.from(selectedIds).some((id) => {
      const user = users.find((u) => u.id === id);
      return user?.status === 'pending';
    });
  }, [selectedIds, users]);

  const handleInviteUser = useCallback(
    (data: { email: string; role: CustomerRole; displayName?: string }) => {
      inviteUserMutation.mutate(data, {
        onSuccess: () => {
          showSuccess('Invitation sent', `Invite sent to ${data.email}`);
          setIsInviteModalOpen(false);
        },
        onError: () => {
          showError('Failed to send invite', 'Please try again');
        },
      });
    },
    [inviteUserMutation, showSuccess, showError],
  );

  const handleUpdateUser = useCallback(
    (data: { email?: string; displayName?: string; role?: CustomerRole }) => {
      if (!selectedUser) return;

      updateUserMutation.mutate(
        { userId: selectedUser.id, ...data },
        {
          onSuccess: () => {
            showSuccess('User updated', 'User details have been saved');
            setIsEditDrawerOpen(false);
            setSelectedUser(null);
          },
          onError: () => {
            showError('Failed to update user', 'Please try again');
          },
        },
      );
    },
    [selectedUser, updateUserMutation, showSuccess, showError],
  );

  const handleDeleteUser = useCallback(() => {
    if (!selectedUser) return;

    const isPending = selectedUser.status === 'pending';
    const email = selectedUser.email;

    deleteUserMutation.mutate(selectedUser.id, {
      onSuccess: () => {
        const message = isPending ? 'Invite cancelled' : 'User deactivated';
        showSuccess(message, `${email} has been removed`);
        setIsDeleteDialogOpen(false);
        setIsEditDrawerOpen(false);
        setSelectedUser(null);
      },
      onError: () => {
        showError('Failed to delete user', 'Please try again');
      },
    });
  }, [selectedUser, deleteUserMutation, showSuccess, showError]);

  const handleResendInvite = useCallback(
    (user?: ManagedUser) => {
      const targetUser = user || selectedUser;
      if (!targetUser) return;

      resendInviteMutation.mutate(targetUser.id, {
        onSuccess: () => {
          showSuccess(
            'Invite resent',
            `New invite sent to ${targetUser.email}`,
          );
        },
        onError: () => {
          showError('Failed to resend invite', 'Please try again');
        },
      });
    },
    [selectedUser, resendInviteMutation, showSuccess, showError],
  );

  const handleBulkAction = useCallback(
    (action: BulkAction) => {
      const userIds = Array.from(selectedIds);

      bulkUsersMutation.mutate(
        { action, userIds },
        {
          onSuccess: (result) => {
            const actionText =
              action === 'delete' ? 'deleted' : 'resent invites to';
            showSuccess(
              'Bulk operation complete',
              `Successfully ${actionText} ${result.processed} user(s)`,
            );
            setSelectedIds(new Set());
          },
          onError: () => {
            showError(
              'Bulk operation failed',
              'Some operations may have failed',
            );
          },
        },
      );
    },
    [selectedIds, bulkUsersMutation, showSuccess, showError],
  );

  const handleEditUser = useCallback((user: ManagedUser) => {
    setSelectedUser(user);
    setIsEditDrawerOpen(true);
  }, []);

  const handleDeleteClick = useCallback((user: ManagedUser) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleCloseEditDrawer = useCallback(() => {
    setIsEditDrawerOpen(false);
    setSelectedUser(null);
  }, []);

  // Pagination handlers
  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setDisplayCount(newSize);
  }, []);

  const handleShowMore = useCallback(() => {
    setDisplayCount((prev) => Math.min(prev + pageSize, filteredUsers.length));
  }, [pageSize, filteredUsers.length]);

  return (
    <>
      <Box
        bg="surface.card"
        borderRadius="16px"
        border="1px solid"
        borderColor="border.layout"
        w="100%"
        p={6}>
        <HStack justify="space-between" align="center" mb={6}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="lg"
            color="text.title">
            USER MANAGEMENT
          </Text>
          <Button
            bg="icon.green"
            color="white"
            size="md"
            px={4}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="invite-new-user"
            fontFamily="marketingPage"
            onClick={() => setIsInviteModalOpen(true)}
            _hover={{ bg: 'green.600' }}>
            <Icon iconName="plus" boxSize={4} mr={2} color="white" />
            Invite New User
          </Button>
        </HStack>

        {/* Search */}
        <Box mb={4}>
          <SearchBox
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search"
            maxWidth="400px"
          />
        </Box>

        {/* Bulk Actions */}
        <BulkActionsBar
          selectedCount={selectedIds.size}
          onBulkAction={handleBulkAction}
          onClearSelection={() => setSelectedIds(new Set())}
          isLoading={bulkUsersMutation.isLoading}
          hasPendingUsers={hasPendingInSelection}
        />

        {/* Content */}
        {isError ? (
          <Center py={16}>
            <Text color="red.500">
              We were unable to load users. Please try again later.
            </Text>
          </Center>
        ) : isLoading ? (
          <Center py={16}>
            <Spinner color="icon.green" size="lg" />
          </Center>
        ) : (
          <UsersTable
            users={paginatedUsers}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onEdit={handleEditUser}
            onDelete={handleDeleteClick}
            onResendInvite={handleResendInvite}
            totalUsers={filteredUsers.length}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            onShowMore={handleShowMore}
            hasMore={hasMoreUsers}
          />
        )}
      </Box>

      {/* Modals & Dialogs */}
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSubmit={handleInviteUser}
        isLoading={inviteUserMutation.isLoading}
      />

      <EditUserDrawer
        isOpen={isEditDrawerOpen}
        onClose={handleCloseEditDrawer}
        user={selectedUser}
        onSave={handleUpdateUser}
        onResendInvite={() => handleResendInvite()}
        isSaving={updateUserMutation.isLoading}
        isResending={resendInviteMutation.isLoading}
      />

      <DeleteUserDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteUser}
        user={selectedUser}
        isLoading={deleteUserMutation.isLoading}
      />

      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        duration={toastState.duration}
        onClose={hideToast}
        position={{ top: '90px', right: '20px' }}
      />
    </>
  );
};
